import { apiClient } from "./api-client";
import { toArray } from "./api-utils";
import type { AddProjectRequest, EditProjectRequest, ProjectOwnershipTransferRequest } from "./api-types";
import type { ProjectsUsersAndGroups, TriyoProject } from "@/types/models";

export type ProjectCategory = "Active" | "Completed" | "Archived";

export const ProjectsApi = {
  /** Every project the user can see, unpaged. */
  getUserProjects: async (): Promise<TriyoProject[]> => {
    const res = await apiClient.get<unknown>("/projects/getuserprojects");
    return toArray<TriyoProject>(res.data, "Projects", "projects");
  },

  /** Complete status-specific project list from the endpoint used by Triyo.Web. */
  getProjectsForCategory: async (category: ProjectCategory): Promise<TriyoProject[]> => {
    const res = await apiClient.get<unknown>("/Projects/GetProjectsForTelerikGrid", {
      params: { status: category },
    });
    return toArray<TriyoProject>(res.data, "Projects", "projects");
  },

  /** Full project payload used by Triyo.Web's details/edit route. */
  getProject: async (projectId: number): Promise<TriyoProject> => {
    const res = await apiClient.get<TriyoProject>(`/projects/GetProject/${projectId}`);
    return res.data ?? {};
  },

  /** Members/owners roster for the Create Deal wizard (requires Projects/View). */
  getUsersAndGroups: async (): Promise<ProjectsUsersAndGroups> => {
    const res = await apiClient.get<ProjectsUsersAndGroups>("/projects/GetProjectsUsersAndGroups");
    return res.data ?? {};
  },

  /** Creates a project ("Deal"); resolves the created project with its new Id. */
  addProject: async (payload: AddProjectRequest): Promise<TriyoProject> => {
    const res = await apiClient.post<TriyoProject>("/projects/AddProject", payload);
    return res.data ?? {};
  },

  /** Updates a project through the same endpoint as Triyo.Web. */
  editProject: async (payload: EditProjectRequest): Promise<TriyoProject> => {
    const res = await apiClient.patch<TriyoProject>("/projects/EditProjectWithTaskDueDateExtension", payload);
    return res.data ?? {};
  },

  /** Runs Triyo.Web's single-owner transfer workflow, including Task ownership choice. */
  transferProjectOwnership: async (payload: ProjectOwnershipTransferRequest): Promise<boolean> => {
    const res = await apiClient.post<boolean>("/Projects/TransferProjectOwnership", payload);
    return res.data === true;
  },
};
